/**
 * features/terms/tabs/ExamTitlesTab.tsx
 * ------------------------------------------------------------------
 * Per-term exam-column titles. Lifted from ExamTitlePanel which
 * showed all three terms; here scoped to one term, with one input
 * per form level (1..5, AS, A2).
 *
 * Does NOT change grading math — only the header text on the
 * printed report. Blank defaults to "Exam".
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { doc } from 'firebase/firestore';
// Batch C: undefined-safe write + save modal
// (swap only — per-form saves happen rapidly and would make the
// modal feel intrusive. Inline busy state is appropriate.)
import { setDoc } from '@/lib/firebase';
import { Save, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { SCHOOL, type TermKey } from '@/lib/school.config';
import { getExamTitle, validateExamTitle } from '@/lib/assessmentConfig';
import { Card } from '@/components/ui/primitives';

const FORM_LEVELS = ['1', '2', '3', '4', '5', 'AS', 'A2'] as const;
type FormLevel = (typeof FORM_LEVELS)[number];

interface Props {
  term: TermKey;
}

export default function ExamTitlesTab({ term }: Props) {
  const { userData } = useAuth();
  const { examTitles } = useData();
  const [draft, setDraft] = useState<Record<FormLevel, string>>(emptyDraft);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    const next = emptyDraft();
    for (const form of FORM_LEVELS) {
      next[form] = getExamTitle(form, term, examTitles);
    }
    setDraft(next);
  }, [examTitles, term]);

  function setLabel(form: FormLevel, value: string) {
    setDraft((d) => ({ ...d, [form]: value }));
  }

  async function save(form: FormLevel) {
    const title = (draft[form] ?? '').trim();
    const validation = validateExamTitle(title);
    if (!validation.ok) {
      setMsg({ ok: false, text: validation.errors.join(' · ') });
      return;
    }
    const id = `${form}_${term}`;
    setBusy(form);
    setMsg(null);
    try {
      await setDoc(
        doc(db, COLLECTIONS.examTitles, id),
        {
          id,
          formLevel: form,
          term,
          title,
          updatedAt: new Date().toISOString(),
          updatedBy: userData?.uid ?? '',
        },
        { merge: true },
      );
      setMsg({
        ok: true,
        text: `Form ${form} · ${SCHOOL.terms[term].label}: saved as "${title}".`,
      });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="p-5">
      <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">
        Exam column titles
      </h3>
      <p className="text-xs text-gray-500 mb-1">
        The exam column on the printed report card uses these titles per form level. The grading math does not depend on this — only the header text changes. Blank fields default to "Exam".
      </p>
      <p className="text-xs text-gray-400 mb-4">
        Term 1 typically has no exam (the term mark is the CA average); these titles only appear on a printed report when the term has an exam value.
      </p>

      {msg && (
        <div className={`mb-4 text-sm rounded-xl px-3 py-2.5 border flex items-center gap-2 ${
          msg.ok
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
            : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
        }`}>
          {msg.ok ? <Check size={15} /> : <AlertTriangle size={15} />}
          {msg.text}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {FORM_LEVELS.map((form) => (
          <div
            key={form}
            className="border border-gray-200 dark:border-gray-700 rounded-xl p-3"
          >
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Form {form}
            </label>
            <input
              value={draft[form] ?? ''}
              onChange={(e) => setLabel(form, e.target.value)}
              maxLength={40}
              placeholder="Exam"
              className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 text-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all"
            />
            <button
              onClick={() => save(form)}
              disabled={busy !== null}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
            >
              {busy === form ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function emptyDraft(): Record<FormLevel, string> {
  const out = {} as Record<FormLevel, string>;
  for (const f of FORM_LEVELS) out[f] = '';
  return out;
}
